<script lang="ts">
  // components
  import { TaskPanel } from "@getprovi/craft-svelte";
  import { get } from "svelte/store";
  import ApiKeyForm from "@retailer/svelte/ControlStateUserAuthentication/Ohlq/ApiKeyForm.svelte";
  import Connect from "@retailer/svelte/ControlStateUserAuthentication/Ohlq/Connect.svelte";
  import Success from "@retailer/svelte/ControlStateUserAuthentication/Ohlq/Success.svelte";
  import { createEventDispatcher, onDestroy } from "svelte";

  // stores
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import RetailerUserAuthenticationModalStore from "@retailer/svelte/stores/RetailerUserAuthenticationModalStore";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";

  // services
  import { getRetailerUserAuthenticationStatus } from "@shared/services/retailer_user_authentication_service";
  import {
    createRetailerUserAuthentication,
    loadRetailerUserAuthentications,
  } from "@retailer/services/retailer_user_authentications_service";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { RetailerUserAuthenticationEventEntity } from "@shared/services/event_tracking/entities/retailer_user_authentication_event_entity";
  import { OHLQ_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

  export let open: boolean = false;
  export let distributorId: number = null;

  const dispatch = createEventDispatcher();

  let apiEmail: string;
  let apiKey: string;
  let invalid: boolean;
  let title: string;

  let activeStep: string = "";
  let authStatus: string;
  let invalidState: boolean = false;
  let validationError: string = "";

  const unsubscribe = RetailerUserAuthenticationModalStore.subscribe(n => {
    if (open != n.open && n.open) {
      activeStep = "";
      if (authStatus == "invalid") {
        activeStep = "apiKey";
      }
    }
    open = n.open;
    distributorId = n.distributorId;
    invalidState = false;
    validationError = "";
    if (!distributorId) {
      let distributorIds = get(DistributorsStore)
        .filter(d => d.require_retailer_user_authentication)
        .map(d => d.id);
      distributorId = distributorIds[0] || OHLQ_DISTRIBUTOR_ID;
    }
  });

  onDestroy(() => {
    unsubscribe();
  });

  function currentStep() {
    if (authStatus == "valid") {
      return "success";
    } else if (activeStep == "apiKey") {
      return "apiKey";
    } else {
      return "intro";
    }
  }
  function closeModal() {
    open = false;
    invalidState = false;
    validationError = "";
    primaryBtnProps.disabled = false;
    let distributors = get(DistributorsStore);
    authStatus = getRetailerUserAuthenticationStatus($RetailerUserAuthenticationsStore, [
      distributors.find(d => d.id == distributorId),
    ]);
    RetailerUserAuthenticationModalStore.set({ open: false, distributorId: null });

    let entity = new RetailerUserAuthenticationEventEntity({
      distributorId: distributorId,
      step: currentStep(),
      authenticationStatus: authStatus,
    });
    EventTracking.trackClick("retailer_user_authentication_close_authentication_flow", [entity]);

    dispatch("close");
  }

  function findTitle(status: string): string {
    switch (status) {
      case "valid":
        return "Account Connected";
      default:
        return "Connect Account";
    }
  }

  async function primaryClick() {
    let step: string;
    let entity = new RetailerUserAuthenticationEventEntity({
      distributorId: distributorId,
      step: currentStep(),
      authenticationStatus: authStatus,
    });
    EventTracking.trackClick("retailer_user_authentication_flow_continue", [entity]);

    if (authStatus == "pending" && !activeStep) {
      activeStep = "apiKey";
    } else if (activeStep == "apiKey") {
      invalidState = false;
      validationError = "";

      primaryBtnProps.showLoader = true;
      await attemptCreateRetailerUserAuthentication();
      dispatch("update");
      primaryBtnProps.showLoader = false;
    } else {
      closeModal();
    }
  }

  async function attemptCreateRetailerUserAuthentication() {
    if (!!apiKey && !!apiEmail && !invalid) {
      let res = await createRetailerUserAuthentication(distributorId, apiEmail, apiKey);
      if (res.status == 422) {
        let json = await res.json();
        invalidState = true;
        validationError = json?.errors?.messages?.join("\n");
      } else {
        await loadRetailerUserAuthentications();
        let auth = get(RetailerUserAuthenticationsStore)?.find(
          a => a.distributor_id == distributorId,
        );
        if (auth?.status == "pending" || auth?.status == "invalid") {
          invalidState = true;
          if (auth?.validation) {
            validationError = auth?.validation;
          }
          let entity = new RetailerUserAuthenticationEventEntity({
            distributorId: distributorId,
            step: "apiKey",
            authenticationStatus: "pending",
          });
          EventTracking.trackClick("retailer_user_authentication_flow_failed", [entity]);
        } else {
          activeStep = "";
        }
      }
    }
  }

  $: {
    let distributors = get(DistributorsStore);
    authStatus = getRetailerUserAuthenticationStatus($RetailerUserAuthenticationsStore, [
      distributorId,
    ]);
    if (authStatus == "invalid") {
      activeStep = "apiKey";
    }
  }

  $: title = findTitle(authStatus);

  let primaryBtnProps = { showLoader: false, disabled: false };
</script>

<div class="craft fizz-styles">
  <TaskPanel
    bind:open
    clickOutsideCallback={closeModal}
    closeBtnClick={closeModal}
    id="ohlq-auth-task-panel"
    {primaryClick}
    primaryLabel={"Continue"}
    {primaryBtnProps}
    secondaryLabel={null}
    testId="ohlq-auth-task-panel"
    {title}
  >
    {#if activeStep == "apiKey"}
      <ApiKeyForm
        bind:apiKey
        bind:apiEmail
        bind:invalid
        bind:primaryBtnProps
        bind:invalidState
        bind:validationError
      />
    {:else if authStatus == "pending"}
      <Connect on:continue={primaryClick} />
    {:else if authStatus == "valid"}
      <Success {distributorId} />
    {/if}
  </TaskPanel>
</div>
